import type { Language } from '@/lib/i18n';

export type CountryCode = 'GH' | 'CI' | 'SN' | 'TG' | 'BJ';

export interface CountryProfile {
  code: CountryCode;
  name: string;
  ministry: string;
  utility: string;
  ruralAgency: string;
  regulator: string;
  program: string;
  currency: string;
  reqPrefix: string;
  dataScale: number;
  accessPercent: number;
  targetPercent: number;
  households: number;
  newThisMonth: number;
  baseCumulative: number;
  warehouses: [string, string, string];
  contractors: [string, string, string, string, string];
  regions: [string, string, string, string, string];
  officialLanguage: Language;
}

export const countryProfiles: Record<CountryCode, CountryProfile> = {
  GH: {
    code: 'GH',
    name: 'Ghana',
    ministry: 'Ministry of Energy',
    utility: 'ECG / NEDCo',
    ruralAgency: 'Ghana Energy Commission',
    regulator: 'PURC',
    program: 'NEP / PEPT',
    currency: 'GHS',
    reqPrefix: 'MOEN-REQ',
    dataScale: 1.0,
    accessPercent: 84,
    targetPercent: 100,
    households: 121342,
    newThisMonth: 316,
    baseCumulative: 298052,
    warehouses: [
      'Main Warehouse - Tema',
      'Regional Depot - Kumasi',
      'Field Store - Tamale',
    ],
    contractors: [
      'Volta River Authority',
      'Kumasi Metropolitan Assembly',
      'Northern Electricity Distribution Co.',
      'ECG - Eastern Region',
      'Western Region Electrification Proj.',
    ],
    regions: ['Greater Accra', 'Ashanti', 'Northern', 'Eastern', 'Western'],
    officialLanguage: 'en',
  },
  CI: {
    code: 'CI',
    name: "Cote d'Ivoire",
    ministry: "Ministère des Mines, du Pétrole et de l'Énergie",
    utility: 'CIE',
    ruralAgency: 'CI-ENERGIES',
    regulator: 'ANARE-CI',
    program: 'PEPT',
    currency: 'XOF',
    reqPrefix: 'CIE-REQ',
    dataScale: 0.72,
    accessPercent: 68,
    targetPercent: 100,
    households: 52340,
    newThisMonth: 284,
    baseCumulative: 214880,
    warehouses: [
      'Entrepôt Principal - Abidjan',
      'Dépôt Régional - Bouaké',
      'Magasin de Terrain - Korhogo',
    ],
    contractors: [
      'CI-ENERGIES Lagunes',
      'Mairie de Bouake',
      'CIE Vallee du Bandama',
      'Direction Regionale Savanes',
      'Projet Electrification San Pedro',
    ],
    regions: ['Lagunes', 'Vallee du Bandama', 'Savanes', 'Bas-Sassandra', 'Comoe'],
    officialLanguage: 'fr',
  },
  SN: {
    code: 'SN',
    name: 'Senegal',
    ministry: 'Ministère du Pétrole et des Énergies',
    utility: 'SENELEC',
    ruralAgency: 'ASER',
    regulator: 'CRSE',
    program: 'SE4ALL / PNUAE',
    currency: 'XOF',
    reqPrefix: 'SEN-REQ',
    dataScale: 0.85,
    accessPercent: 76,
    targetPercent: 100,
    households: 71820,
    newThisMonth: 312,
    baseCumulative: 246410,
    warehouses: [
      'Entrepôt Principal - Dakar',
      'Dépôt Régional - Thiès',
      'Magasin de Terrain - Kaolack',
    ],
    contractors: [
      'SENELEC Dakar',
      'ASER Thies',
      'Concessionnaire Kaolack',
      'Direction Saint-Louis',
      'Projet Rural Casamance',
    ],
    regions: ['Dakar', 'Thies', 'Kaolack', 'Saint-Louis', 'Ziguinchor'],
    officialLanguage: 'fr',
  },
  TG: {
    code: 'TG',
    name: 'Togo',
    ministry: 'Ministère des Mines et des Énergies',
    utility: 'CEET',
    ruralAgency: 'AT2ER',
    regulator: 'ARSE',
    program: 'Tinga / PNUAE',
    currency: 'XOF',
    reqPrefix: 'CEET-REQ',
    dataScale: 0.38,
    accessPercent: 46,
    targetPercent: 100,
    households: 28910,
    newThisMonth: 186,
    baseCumulative: 118240,
    warehouses: [
      'Entrepôt Principal - Lomé',
      'Dépôt Régional - Kara',
      'Magasin de Terrain - Dapaong',
    ],
    contractors: [
      'CEET Maritime',
      'AT2ER Kara',
      'Direction Centrale Sokode',
      "Mairie d'Atakpame",
      'Projet Rural Savanes',
    ],
    regions: ['Maritime', 'Kara', 'Centrale', 'Plateaux', 'Savanes'],
    officialLanguage: 'fr',
  },
  BJ: {
    code: 'BJ',
    name: 'Benin',
    ministry: "Ministère de l'Énergie",
    utility: 'SBEE',
    ruralAgency: 'ABERME',
    regulator: 'ARE',
    program: 'DGRE / PADSBEE',
    currency: 'XOF',
    reqPrefix: 'SBEE-REQ',
    dataScale: 0.32,
    accessPercent: 41,
    targetPercent: 100,
    households: 23640,
    newThisMonth: 142,
    baseCumulative: 96730,
    warehouses: [
      'Entrepôt Principal - Cotonou',
      'Dépôt Régional - Parakou',
      'Magasin de Terrain - Natitingou',
    ],
    contractors: [
      'SBEE Littoral',
      'ABERME Borgou',
      'DGRE Oueme',
      'Direction Regionale Zou',
      'Projet Rural Atacora',
    ],
    regions: ['Littoral', 'Borgou', 'Oueme', 'Zou', 'Atacora'],
    officialLanguage: 'fr',
  },
};

export const countryList: CountryProfile[] = [
  countryProfiles.GH,
  countryProfiles.CI,
  countryProfiles.SN,
  countryProfiles.TG,
  countryProfiles.BJ,
];
